import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { CallRouteUsage } from '@weave/schema-gen-ts/dist/schemas/phone/callqueue-config/v1/callqueue_config.pb';
import { ReadResponse } from '@weave/schema-gen-ts/dist/schemas/phone/callqueue-config/v1/callqueue_config_service.pb';
import { useQuery, useQueryClient } from 'react-query';
import CallQueueAPI from '@frontend/api-call-queue';
import { Trans, useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  Heading,
  ModalControlResponse,
  Tray,
  TextField,
  useForm,
  BannerNotification,
  TextLink,
  Button,
  useAlert,
  useModalControl,
  Modal,
  Text,
  Truncated,
  ModalControlModalProps,
  FormFieldActionTypes,
} from '@frontend/design-system';
import { useCallQueueCacheUpdater } from '../../hooks/useCallQueueCacheUpdater';
import { queryKeys } from '../../query-keys';
import { useAudioQuery } from '../../utils/use-audio-query';
import { AudioItem } from '../audio-picker/types';
import {
  AssignDeviceTableWithModal,
  RoutingOptions,
  CallerExperienceOptions,
  LocationsOptions,
} from '../call-queue/new/call-queue-page';
import { populateSchema, createSubmitPayload } from '../call-queue/new/schema';
import { useCallQueueMutations } from '../call-queue/new/use-call-queue-mutations';

type Props = {
  addStepModalProps?: ModalControlModalProps;
  modalControls: ModalControlResponse;
  mountTarget: string;
  selectedCallQueue?: ReadResponse;
  stepContext?: {
    onCancelClick?: () => void;
    onClose?: () => void;
  };
};

type ContentProps = {
  selectedCallQueue: ReadResponse;
  modalControls: ModalControlResponse;
  addStepModalProps?: ModalControlModalProps;
  stepContext?: {
    onCancelClick?: () => void;
    onClose?: () => void;
  };
};

const CallQueueSettingTrayContent = ({
  selectedCallQueue,
  modalControls,
  addStepModalProps,
  stepContext,
}: ContentProps) => {
  const { t } = useTranslation('phone');
  const queryClient = useQueryClient();
  const callQueueId = selectedCallQueue.callQueueId || '';
  const alert = useAlert();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { modalProps: usageModalProps, triggerProps: usageTriggerProps } = useModalControl();
  const { selectedGroupId } = useAppScopeStore();
  const { updateCallRouteCaches } = useCallQueueCacheUpdater(callQueueId);
  const { update } = useCallQueueMutations(settingsTenantLocation?.phoneTenantId ?? '');

  const { data: usageData = [] } = useQuery({
    queryKey: queryKeys.callQueueUsageV2(callQueueId),
    queryFn: () => CallQueueAPI.UsageV2({ callQueueId }),
    select: (data) => data?.usages || [],
    staleTime: 0,
    enabled: !!callQueueId,
  });

  const callRouteUsages = usageData.filter((usage) => 'callRouteUsage' in usage);

  const { data: audioData } = useAudioQuery({
    tenantId: settingsTenantLocation?.phoneTenantId ?? '',
    allowedOptions: { custom: true, standard: true },
  });

  // Helper function to determine if hold music is system/standard media
  const getHoldMusicSystemMedia = useCallback(
    (holdMusicId: string) => {
      const holdMusicData = queryClient.getQueryData<{ standardMedia: AudioItem[] }>(
        [
          settingsTenantLocation?.phoneTenantId,
          ...queryKeys.audioPicker({
            add: true,
            custom: true,
            standard: true,
            tenantId: settingsTenantLocation?.phoneTenantId,
          }),
        ],
        {}
      )?.standardMedia;

      // Use audioData as fallback if cached data is undefined due to race condition
      const standardMedia = holdMusicData || audioData?.standardMedia;
      const flattened = standardMedia ? Object.values(standardMedia).flat() : [];
      return flattened.some((media) => media.id === holdMusicId);
    },
    [queryClient, settingsTenantLocation?.phoneTenantId, audioData]
  );

  const populatedSchema = useMemo(() => {
    const callQueueData = selectedCallQueue;

    /**
     * We initialize the holdMusicSystemMedia field based on the holdMusicId value
     * */
    const holdMusicSystemMedia = getHoldMusicSystemMedia(callQueueData.holdMusicId ?? '');

    return populateSchema({
      tenantId: callQueueData.tenantId ?? '',
      locationIds: callQueueData.locationIds ?? [],
      name: callQueueData.name ?? '',
      devices: callQueueData.devices ?? [],
      deviceRingType: callQueueData.deviceRingType ?? ('' as any),
      greetingMediaId: callQueueData.greetingMediaId ?? '',
      greetingEnabled: callQueueData.greetingEnabled ?? false,
      allowExit: callQueueData.allowExit ?? false,
      positionAnnouncement: callQueueData.positionAnnouncement ?? ('' as any),
      positionAnnouncementEnabled: callQueueData.positionAnnouncementEnabled ?? false,
      holdMusicId: callQueueData.holdMusicId ?? '',
      maxWaitTimeMs: callQueueData.maxWaitTimeMs ?? 0,
      holdMusicSystemMedia,
    });
  }, [selectedCallQueue, getHoldMusicSystemMedia]);

  const form = useForm({
    fields: populatedSchema,
    computeChangedValues: true,
    /**
     * This reducer sets the holdMusicSystemMedia value based on the holdMusicId value
     * The holdMusicSystemMedia field is hidden to the user
     * */
    fieldStateReducer: (state, action) => {
      if (action.type === FormFieldActionTypes.Update && action.payload.name === 'holdMusicId') {
        const holdMusicSystemMedia = getHoldMusicSystemMedia(action.payload.value);

        return {
          ...state,
          holdMusicSystemMedia: { ...state.holdMusicSystemMedia, value: holdMusicSystemMedia },
        };
      }
      return state;
    },
  });

  const nameField = form.getFieldProps('name');

  const handleSave = useCallback(() => {
    const finalState = createSubmitPayload({
      state: selectedCallQueue,
      formValues: form.values,
    });

    const holdMusicSystemMedia = getHoldMusicSystemMedia(form.values.holdMusicId ?? '');
    finalState.holdMusicSystemMedia = holdMusicSystemMedia;

    update.mutate(finalState, {
      onSuccess: () => {
        const greetingMediaId = finalState.greetingMediaId;
        const greetingMedia = audioData?.customMedia.find((audio) => audio.id === greetingMediaId);

        updateCallRouteCaches({
          callQueueName: finalState.name,
          greetingMediaItemId: finalState.greetingMediaId,
          greetingMediaFileName: greetingMedia?.name,
        });

        alert.success(t('Call Queue updated successfully.'));
        modalControls.closeModal();
      },
      onError: () => {
        alert.error(t('System failed to update Call Queue. Please try again.'));
      },
    });
  }, [
    selectedCallQueue,
    form,
    update,
    alert,
    t,
    modalControls,
    audioData,
    updateCallRouteCaches,
    getHoldMusicSystemMedia,
  ]);

  const handleCancel = useCallback(() => {
    modalControls.closeModal();
  }, [modalControls]);

  return (
    <>
      <Tray.Header>{t('Edit Call Queue')}</Tray.Header>
      <Tray.Body>
        <Heading level={3} css={{ marginBottom: !!callRouteUsages?.length ? theme.spacing(1) : theme.spacing(2) }}>
          {t('Settings')}
        </Heading>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          {!!callRouteUsages?.length && (
            <BannerNotification
              status='info'
              message={
                <Trans t={t}>
                  This Call Queue is being used in{' '}
                  <TextLink as='span' href='#' css={{ display: 'inline' }} onClick={usageTriggerProps.onClick}>
                    {t('{{count}} different step', { count: callRouteUsages.length })}
                  </TextLink>{' '}
                  in your Call Routes. Any changes you make here will be reflected in all of those places.
                </Trans>
              }
            />
          )}
          <TextField {...nameField} label={t('Call Queue Name')} name='name' css={{ maxWidth: theme.spacing(42) }} />
          {settingsTenantLocation && (
            <AssignDeviceTableWithModal
              callQueueId={selectedCallQueue.callQueueId || ''}
              assignedDevices={selectedCallQueue.devices || []}
              tenantLocation={settingsTenantLocation}
              tableLayout='combined'
            />
          )}
          <RoutingOptions form={form} condensed tableLayout='combined' />
          <CallerExperienceOptions
            form={form}
            tenantId={settingsTenantLocation?.phoneTenantId ?? ''}
            condensed
            tableLayout='combined'
          />

          {settingsTenantLocation?.locationType === 'unify' && !!selectedGroupId && (
            <>
              <div css={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <Heading level={3}>{t('Locations (Optional)')}</Heading>
                <Text>{t("Select the locations whose users can access this queue's real-time data.")}</Text>
              </div>
              <LocationsOptions
                form={form}
                options={
                  settingsTenantLocation?.children?.map((location) => ({
                    label: location.name ?? '',
                    value: location.locationId ?? '',
                  })) ?? []
                }
                tableLayout='combined'
              />
            </>
          )}

          <div
            css={css`
              display: flex;
              gap: ${theme.spacing(2)};
              justify-content: flex-end;
            `}
          >
            <Button size='large' variant='secondary' onClick={handleCancel}>
              {t('Cancel')}
            </Button>
            <Button
              size='large'
              css={{ width: '62px' }}
              variant='primary'
              onClick={handleSave}
              loading={update.isLoading}
              trackingId='phone::setting::call-route:save-callqueue-btn'
            >
              {t('Save')}
            </Button>
          </div>
        </div>
      </Tray.Body>

      <CallQueueUsageModal
        modalProps={usageModalProps}
        usageData={callRouteUsages || []}
        callQueueId={callQueueId}
        modalControls={modalControls}
        addStepModalProps={addStepModalProps}
        stepContext={stepContext}
      />
    </>
  );
};

export const CallQueueSettingTray = ({
  addStepModalProps,
  modalControls,
  mountTarget,
  selectedCallQueue,
  stepContext,
}: Props) => {
  return (
    <Tray
      mountTarget={mountTarget}
      width='xlarge'
      zIndex={10001}
      css={css`
        padding: ${theme.spacing(4)};
        border-top: 1px solid ${theme.colors.neutral20};
        overflow-y: auto;
      `}
      {...modalControls.modalProps}
    >
      {modalControls.modalProps.show && selectedCallQueue && (
        <CallQueueSettingTrayContent
          selectedCallQueue={selectedCallQueue}
          modalControls={modalControls}
          addStepModalProps={addStepModalProps}
          stepContext={stepContext}
        />
      )}
    </Tray>
  );
};

const CallQueueUsageModal = ({
  modalProps,
  usageData,
  callQueueId,
  modalControls,
  stepContext,
}: {
  modalProps: any;
  usageData: {
    callRouteUsage: CallRouteUsage;
  }[];
  callQueueId: string;
  modalControls: ModalControlResponse;
  addStepModalProps?: ModalControlModalProps;
  stepContext?: {
    onCancelClick?: () => void;
    onClose?: () => void;
  };
}) => {
  const { t } = useTranslation('phone');
  const { navigate } = useSettingsNavigate();

  return (
    <Modal {...modalProps} minWidth={600}>
      <Modal.Header onClose={modalProps.onClose}>{t('Call Queue Usage')}</Modal.Header>
      <Modal.Body>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          <Text>{t('This Call Queue is currently assigned to the following Call Routes:')}</Text>
          <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {usageData.map(({ callRouteUsage: { callRouteId, callRouteName, usageCount } }) => (
              <div key={callRouteId} css={{ display: 'flex', gap: '4px', alignItems: 'center', width: '500px' }}>
                <div css={{ flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
                  <Truncated
                    as={TextLink}
                    textAlign='left'
                    onClick={() => {
                      modalProps.onClose();
                      modalControls.closeModal();
                      stepContext?.onCancelClick?.();
                      stepContext?.onClose?.();
                      navigate({
                        to: '/phone/call-routes/:id',
                        params: { id: callRouteId },
                        search: { callObjectId: callQueueId },
                      });
                    }}
                  >
                    {callRouteName}
                  </Truncated>
                </div>
                {usageCount !== undefined && usageCount > 1 && (
                  <span css={{ whiteSpace: 'nowrap', flexShrink: 0 }}>({usageCount} steps)</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        secondary={{
          label: t('Close'),
          onClick: modalProps.onClose,
        }}
      />
    </Modal>
  );
};
